import { useState } from 'react';
import { Button } from '@/ui/components/ui/button';
import { Label } from '@/ui/components/ui/label';
import { Textarea } from '@/ui/components/ui/textarea';
import type { ProjectObject } from '../../../hooks/use-admin';

interface AllowedOriginsFormProps {
	project: ProjectObject;
	updateProject: (opts: {
		variables: { id: string; input: Partial<ProjectObject> };
	}) => Promise<unknown>;
	canEdit: boolean;
}

export function AllowedOriginsForm({
	project,
	updateProject,
	canEdit,
}: AllowedOriginsFormProps) {
	const [originsInput, setOriginsInput] = useState('');
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError(null);
		setSubmitting(true);
		try {
			const origins = originsInput
				.split('\n')
				.map((s) => s.trim())
				.filter(Boolean);
			await updateProject({
				variables: {
					id: project.id,
					input: { allowedOrigins: origins },
				},
			});
		} catch (err) {
			setError(
				err instanceof Error ? err.message : 'Failed to save origins',
			);
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<form onSubmit={handleSubmit} className='flex flex-col gap-2'>
			<div>
				<Label htmlFor='allowed-origins'>Allowed origins</Label>
				<p className='text-xs text-muted-foreground mt-0.5'>
					One per line, e.g. https://acme.com
				</p>
			</div>
			<Textarea
				id='allowed-origins'
				rows={3}
				placeholder={'https://acme.com\nhttps://staging.acme.com'}
				value={
					originsInput || (project.allowedOrigins ?? []).join('\n')
				}
				onChange={(e) => setOriginsInput(e.target.value)}
				className='font-mono text-xs'
			/>
			{error && (
				<p className='text-sm text-destructive rounded-lg bg-destructive/10 px-3 py-2'>
					{error}
				</p>
			)}
			{canEdit && (
				<div>
					<Button type='submit' size='sm' disabled={submitting}>
						{submitting ? 'Saving…' : 'Save origins'}
					</Button>
				</div>
			)}
		</form>
	);
}
